import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router'
import { IconWarningRoundedFilled, Spot, useNotification } from '@n26/nxd'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/features/auth/hooks/useAuth'
import {
  IDLE_ACTIVITY_STORAGE_KEY,
  getLastActivityAt,
  setLastActivityAt,
} from '@/features/auth/model/idleActivityStore'
import { IDLE_LOGOUT_REASON } from '@/features/auth/model/idleLogoutState'
import { SLIM_ROUTES } from '@/shared/routes'

const IDLE_MS = 15 * 60 * 1000
const GRACE_MS = 1 * 60 * 1000
const CHECK_INTERVAL_MS = 15 * 1000
const ACTIVITY_WRITE_THROTTLE_MS = 5 * 1000

const ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
] as const

type Phase = 'active' | 'warning' | 'loggedOut'

// A single long setTimeout gets throttled/suspended once the tab is
// backgrounded, silently breaking the logout — recompute elapsed time from
// a persisted timestamp instead.
export const useIdleTimeout = (): void => {
  const { logout } = useAuth()
  const { notify } = useNotification()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const phaseRef = useRef<Phase>('active')
  const dismissWarningRef = useRef<(() => void) | null>(null)
  const lastWriteRef = useRef(0)

  useEffect(() => {
    const originalTitle = document.title

    const setTitle = (override: string | null): void => {
      document.title = override ?? originalTitle
    }

    const dismissWarning = (): void => {
      dismissWarningRef.current?.()
      dismissWarningRef.current = null
    }

    const showWarning = (): void => {
      const { dismiss } = notify({
        icon: (
          <Spot color='wheat'>
            <IconWarningRoundedFilled />
          </Spot>
        ),
        title: t('auth.idle.warningTitle'),
        description: t('auth.idle.warningDescription'),
        duration: 0,
        id: 'idle-warning',
      })
      dismissWarningRef.current = dismiss
      setTitle(t('titles.idleWarning'))
    }

    const performLogout = (): void => {
      dismissWarning()
      setTitle(t('titles.loggedOut'))
      void logout().then(() => {
        navigate(SLIM_ROUTES.LOGIN, {
          replace: true,
          state: { reason: IDLE_LOGOUT_REASON },
        })
      })
    }

    const setPhase = (next: Phase): void => {
      if (phaseRef.current === next) return
      const previous = phaseRef.current
      phaseRef.current = next

      switch (next) {
        case 'active':
          if (previous === 'warning') dismissWarning()
          setTitle(null)
          return
        case 'warning':
          showWarning()
          return
        case 'loggedOut':
          performLogout()
          return
      }
    }

    const recheck = (): void => {
      const elapsed = Date.now() - getLastActivityAt()

      if (elapsed >= IDLE_MS + GRACE_MS) {
        setPhase('loggedOut')
        return
      }
      if (elapsed >= IDLE_MS) {
        setPhase('warning')
        return
      }
      setPhase('active')
    }

    const recordActivity = (): void => {
      const now = Date.now()
      if (now - lastWriteRef.current < ACTIVITY_WRITE_THROTTLE_MS) return
      lastWriteRef.current = now
      setLastActivityAt(now)
      recheck()
    }

    const handleStorage = (event: StorageEvent): void => {
      if (event.key !== IDLE_ACTIVITY_STORAGE_KEY) return
      recheck()
    }

    // Baseline so a fresh mount (first login, or a newly opened tab with no
    // activity recorded yet) starts the clock now rather than reading back
    // an unset value.
    setLastActivityAt(getLastActivityAt())
    recheck()

    const intervalId = setInterval(recheck, CHECK_INTERVAL_MS)
    ACTIVITY_EVENTS.forEach(event =>
      window.addEventListener(event, recordActivity)
    )
    window.addEventListener('storage', handleStorage)
    document.addEventListener('visibilitychange', recheck)
    window.addEventListener('focus', recheck)

    return () => {
      clearInterval(intervalId)
      ACTIVITY_EVENTS.forEach(event =>
        window.removeEventListener(event, recordActivity)
      )
      window.removeEventListener('storage', handleStorage)
      document.removeEventListener('visibilitychange', recheck)
      window.removeEventListener('focus', recheck)
      dismissWarning()
      if (phaseRef.current !== 'loggedOut') setTitle(null)
    }
  }, [logout, navigate, notify, t])
}
